













import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { MathPart, MathPartTypes } from "@/store/types";
import { newGuid } from "../store/modules/util";
import { Editor } from "./editor";
import { Cleaner } from "./cleaner";
import { NodeManager } from "./node-type";
import { EditorManager, NavigationDirection } from "./editor-manager";
import { CursorSet } from "./cursor-set";
import { CursorMoveHorizontally } from "./cursor-move-horizontally";
import { CursorMoveVertically } from "./cursor-move-vertically";

const KEY_CODES = {
  RIGHT_ARROW: 39,
  LEFT_ARROW: 37,
  BS: 8,
  DEL: 46,
  WHITESPACE: 32,
  LETTER_C: 67,
  LETTER_X: 88,
  LETTER_A: 65
};

enum Place {
  text = "text",
  cell = "cell",
  nothing = "nothing"
}

@Component
export default class TextEditor extends Vue {
  @Prop() mathText!: MathPart[];
  @Prop() initialInsert!: MathPart[] | undefined;
  @Prop() noErrDescription!: string;
  @Prop() copyMathAsPlainText!: boolean;
  @Prop() readonly?: boolean;
  @Prop() wrap?: boolean;

  private editorManager = new EditorManager(
    this.onInput.bind(this),
    this.onMoveOutEditor.bind(this),
    this.onCursorPlace.bind(this),
    () => this.$refs.errorDiv as Element,
    this.noErrDescription
  );

  private pasteListener: ((event: ClipboardEvent) => boolean) | null = null;
  private copyListener: ((event: ClipboardEvent) => boolean) | null = null;
  private cutListener: ((event: ClipboardEvent) => boolean) | null = null;
  private dropListener: ((event: DragEvent) => boolean) | null = null;
  private dragStartListener: ((event: DragEvent) => boolean) | null = null;
  private selectionChangeListener: ((event: Event) => boolean) | null = null;

  private eventHandled = false;

  private readonly nodeManager = new NodeManager(this.editorManager);

  private readonly cleaner = new Cleaner(
    this.editorManager.isEditor.bind(this.editorManager),
    this.nodeManager.isDelimiter.bind(this.nodeManager),
    this.nodeManager.isAllowedTag.bind(this.nodeManager),
    this.nodeManager.isBreak.bind(this.nodeManager)
  );

  private readonly cursorSet = new CursorSet(this.nodeManager);
  private readonly cursorMoveHor = new CursorMoveHorizontally(
    this.nodeManager,
    () => this.element
  );
  private readonly cursorMoveVert = new CursorMoveVertically(
    this.nodeManager,
    () => this.element
  );

  mounted() {
    this.pasteListener = (event: ClipboardEvent) => this.onPaste(event);
    this.copyListener = (event: ClipboardEvent) => this.onCopy(event);
    this.cutListener = (event: ClipboardEvent) => this.onCut(event);
    this.dropListener = (event: DragEvent) => this.onDrop(event);
    this.dragStartListener = (event: DragEvent) => this.onDragStart(event);
    this.selectionChangeListener = (event: Event) =>
      this.onSelectionChange(event);
    this.element.addEventListener("paste", this.pasteListener);
    this.element.addEventListener("copy", this.copyListener);
    this.element.addEventListener("cut", this.cutListener);
    this.element.addEventListener("drop", this.dropListener);
    this.element.addEventListener("dragstart", this.dragStartListener);
    document.addEventListener("selectionchange", this.selectionChangeListener);

    this.onPropertyChanged();
  }

  @Watch("mathText")
  onPropertyChanged() {
    this.editorManager.clear();

    const el = this.$refs.editor as Element;
    el.innerHTML = "";
    const elements = this.mathParts2Dom(this.mathText);
    for (const elem of elements) {
      el.insertBefore(elem, null);
    }
    this.onInput();
  }

  beforeDestroy() {
    if (this.pasteListener) {
      this.element.removeEventListener("paste", this.pasteListener);
    }
    if (this.copyListener) {
      this.element.removeEventListener("copy", this.copyListener);
    }
    if (this.cutListener) {
      this.element.removeEventListener("cut", this.cutListener);
    }
    if (this.dropListener) {
      this.element.removeEventListener("drop", this.dropListener);
    }
    if (this.dragStartListener) {
      this.element.removeEventListener("dragstart", this.dragStartListener);
    }
    if (this.selectionChangeListener) {
      document.removeEventListener(
        "selectionchange",
        this.selectionChangeListener
      );
    }
  }

  forEach(
    onEditor: (editor: Editor, index: number) => void,
    onText: (node: Text, index: number) => void,
    onDelim?: (delim: HTMLSpanElement, index: number) => void,
    onLineBreak?: (lb: HTMLBRElement, index: number) => void
  ) {
    let element = this.$refs.editor as Element;
    let index = 0;
    this.nodeManager.forEachChild(element, {
      visitCell(id: string, editor: Editor) {
        onEditor(editor, index++);
      },
      visitText(el) {
        if (!el.textContent || el.textContent.length == 0) {
          throw new Error("Empty text node found after cleaning!");
        }
        onText(el, index++);
      },
      visitDelimiter(el) {
        if (onDelim) {
          onDelim(el, index++);
        }
      },
      visitLineBreak(el) {
        if (onLineBreak) {
          onLineBreak(el, index++);
        }
      },
      visitServiceBreak() {
        //do nothing
      }
    });
  }

  private onMoveOutEditor(
    editor: Editor,
    del: boolean,
    direction: NavigationDirection
  ) {
    switch (direction) {
      case NavigationDirection.left:
        const left = editor.element.previousSibling;
        if (left) {
          this.cursorSet.setCursorKeepingNode(left, true);
        }
        break;
      case NavigationDirection.right:
        const right = editor.element.nextSibling;
        if (right) {
          this.cursorSet.setCursorKeepingNode(right, false);
        }
        break;
      case NavigationDirection.home:
        if (this.element.childNodes.length > 0) {
          const first = this.element.childNodes[0];
          this.cursorSet.setCursorKeepingNode(first, false);
        }
        break;
      case NavigationDirection.end:
        if (this.element.childNodes.length > 0) {
          const last = this.element.childNodes[
            this.element.childNodes.length - 1
          ];
          this.cursorSet.setCursorKeepingNode(last, true);
        }
        break;
      case NavigationDirection.up:
      case NavigationDirection.down:
        const cursorRect = editor.getCursorBoundingRect();
        this.cursorMoveVert.moveCursorVertically(
          cursorRect,
          editor.element,
          direction === NavigationDirection.up
        );
        break;
    }
    this.eventHandled = true;
  }

  get element(): HTMLElement {
    return this.$refs.editor as HTMLElement;
  }

  private getAnchorPlace(): Place {
    const selection = document.getSelection();

    if (selection) {
      const anchor = selection.anchorNode;
      let element = this.$refs.editor as Element;

      if (anchor && element.contains(anchor)) {
        if (this.editorManager.getEditorByNode(anchor)) {
          return Place.cell;
        } else {
          return Place.text;
        }
      }
    }
    return Place.nothing;
  }

  private getAnchorPosition(): Editor | Node | undefined {
    const selection = document.getSelection();

    if (selection && selection.type === "Caret") {
      const anchor = selection.anchorNode;

      if (anchor && this.element.contains(anchor)) {
        const ed = this.editorManager.getEditorByNode(anchor);
        if (ed) {
          return ed;
        } else {
          const an = selection.anchorNode;
          if (an) {
            if (an === this.element) {
              return this.element.childNodes[selection.anchorOffset];
            } else {
              return an;
            }
          }
        }
      }
    }
  }

  private pasteMathText(mathText: MathPart[]) {
    const self = this;
    const elements = this.mathParts2Dom(this.pastePreprocess(mathText));
    if (elements.length > 0) {
      this.deleteAllSelected();
      this.insertNode(elements[0]);
      const parent = elements[0].parentElement;
      const sibling = elements[0].nextSibling;
      if (parent) {
        for (let i = 1; i < elements.length; i++) {
          parent.insertBefore(elements[i], sibling);
        }
      }
      this.onInput();
      const lastElement = elements[elements.length - 1];
      this.cursorSet.setCursor(lastElement, true);
    }
  }

  private pasteLatex(latex: string) {
    const id = newGuid();
    const editor = this.editorManager.createNewEditor(id,!!this.readonly,!!this.wrap);
    this.deleteAllSelected();
    this.insertNode(editor.element);
    this.onInput();
    this.cursorSet.setCursor(editor.element, true);
    if (editor) {
      editor.paste(latex);
    }
  }

  paste(contentType: string, content: string): boolean {
    if (this.getAnchorPlace() == Place.text) {
      switch (contentType) {
        case "application/x-math-text":
          this.pasteMathText(JSON.parse(content));
          return true;
        case "application/x-latex":
          this.pasteLatex(content);
          return true;
        case "text/plain":
          try {
            const parts: MathPart[] = JSON.parse(content);
            if (parts && parts.length > 0) {
              this.pasteMathText(parts);
              return true;
            } else {
              this.pasteMathText([{ t: MathPartTypes.text, v: content }]);
              return true;
            }
          } catch (e) {
            this.pasteMathText([{ t: MathPartTypes.text, v: content }]);
            return true;
          }
      }
    }

    return false;
  }

  onPaste(e: ClipboardEvent): boolean {
    if (this.readonly) {
      e.preventDefault();
      return false;
    }

    const contentTypes = [
      "application/x-math-text",
      "application/x-latex",
      "text/plain"
    ];

    const cd = e.clipboardData;

    if (cd) {
      for (let contentType of contentTypes) {
        let content = cd.getData(contentType);

        if (content && content.length > 0) {
          if (this.paste(contentType, content)) {
            e.preventDefault();
            e.stopPropagation();
            return false;
          }
        }
      }
    }

    if (
      (window as any)["clipboardData"] &&
      (window as any)["clipboardData"].getData
    ) {
      // IE hack
      const pastedText = (window as any)["clipboardData"].getData("Text");
      if (pastedText) {
        document.execCommand(
          "insertText",
          false,
          pastedText.replace(" ", "\u00A0")
        );
      }

      return false;
    }

    return true;
  }

  //if the selection contain both - text and parts of cells, we will force cells to select all their content
  private onSelectionChange(event: Event) {
    const selection = document.getSelection();
    if (selection) {
      if (selection.type === "Range") {
        if (this.getAnchorPlace() == Place.text) {
          this.editorManager.forEach(editor => {
            if (selection.containsNode(editor.element, true)) {
              editor.selectAll();
            } else {
              editor.deselect();
            }
          });
          const focusNodeEditorElement = getSelectionFocusEditorElement(selection, this.editorManager);
          if (focusNodeEditorElement) {
            extendSelectionToElement(selection, focusNodeEditorElement);
          }
        }
      }
      if (selection.type === "Caret") {
        this.editorManager.forEach(editor => {
          editor.deselect();
        });
      }
    }
    return false;

    function getSelectionFocusEditorElement(selection: Selection, editorManager: EditorManager): Element | null {
      let focusNodeEditor: Editor | null = null;
      if (selection.anchorNode && selection.focusNode) {
        const focusNode = selection.focusNode;
        focusNodeEditor = editorManager.find(editor => {
          return ((editor.element.compareDocumentPosition(focusNode) & Node.DOCUMENT_POSITION_CONTAINED_BY) > 0
                  || editor.element === selection.focusNode);
        });
      }
      return focusNodeEditor ? focusNodeEditor.element : null;
    }

    function extendSelectionToElement(selection: Selection, element: Element) {
      if (selection.anchorNode && selection.focusNode && element.parentElement) {
        const leftToRight = selection.focusNode.compareDocumentPosition(selection.anchorNode) === Node.DOCUMENT_POSITION_PRECEDING;
        const index = Array.prototype.indexOf.call(element.parentElement.childNodes, element);
        selection.extend(element.parentElement, leftToRight ? index + 1 : index);
      }
    }
  }

  onCopy(e: ClipboardEvent) {
    const self = this;
    if (e.clipboardData && e.clipboardData.setData) {
      if (this.getAnchorPlace() == Place.text) {
        const parts = this.copyPreprocess(this.dom2MathParts(true));
        if (hasCells(parts)) {
          const json = JSON.stringify(parts);
          e.clipboardData.setData("application/x-math-text", json);
          e.clipboardData.setData("application/json", json);
          if (this.copyMathAsPlainText) {
            e.clipboardData.setData("text/plain", json);
          } else {
            e.clipboardData.setData("text/plain", "");
          }
        } else if (parts.length > 0) {
          const text = parts.map(p => p.v).join("");
          e.clipboardData.setData("text/plain", text);
        }
      }
      e.preventDefault();
      return false;
    } else {
      return true;
    }

    function hasCells(parts: MathPart[]): boolean {
      return parts.some(
        part =>
          part.t === MathPartTypes.math ||
          part.t === MathPartTypes.grl ||
          part.t === MathPartTypes.tag
      );
    }
  }

  onCut(e: ClipboardEvent) {
    const ret = this.onCopy(e);
    //remove all the selected nodes etc

    if (this.readonly) {
      e.preventDefault();
      return false;
    }

    if (!ret) {
      this.deleteAllSelected();
      this.onInput();
    }

    return ret;
  }

  onDrop(event: DragEvent) {
    if (this.readonly) {
      event.preventDefault();
      return false;
    }

    return true;
  }

  onDragStart(event: DragEvent) {
    if (this.readonly) {
      event.preventDefault();
      return false;
    }

    return true;
  }

  private mathParts2Dom(parts: MathPart[]): Node[] {
    const self = this;

    return parts.map(part => {
      switch (part.t) {
        case MathPartTypes.math: {
          const id = newGuid();

          const editor = self.editorManager.createNewEditor(id, !!self.readonly,!!self.wrap);
          editor.setInternal(part.v);
          editor.validation();
          return editor.element;
        }
        case MathPartTypes.grl: {
          const id = newGuid();

          const editor = self.editorManager.createNewEditor(id, !!self.readonly,!!self.wrap);
          editor.setGRL(part.v);
          editor.validation();
          return editor.element;
        }
        case MathPartTypes.tag: {
          if (part.v === "br") {
            return self.nodeManager.createLineBreak();
          } else {
            throw new Error("Unsupported tag: " + part.v);
          }
        }
        case MathPartTypes.text: {
          return document.createTextNode(part.v.replace(" ", "\u00A0"));
        }
        default: {
          throw new Error("Unknown MathPartType!");
        }
      }
    });
  }

  private pastePreprocess(parts: MathPart[]): MathPart[] {
    return parts.reduce((previousValue: MathPart[], currentValue) => {
      if (currentValue.t === MathPartTypes.text) {
        return previousValue.concat(convertTextsToTags(currentValue));
      }
      previousValue.push(currentValue);
      return previousValue;
    }, []);

    function convertTextsToTags(part: MathPart): MathPart[] {
      return part.v
        .split(/(\n)/g)
        .filter(chunk => chunk.length > 0)
        .map(chunk =>
          chunk === "\n"
            ? { t: MathPartTypes.tag, v: "br" }
            : { t: MathPartTypes.text, v: chunk }
        );
    }
  }

  private copyPreprocess(parts: MathPart[]): MathPart[] {
    return this.concatTexts(convertTagsToTexts(parts));

    function convertTagsToTexts(p: MathPart[]): MathPart[] {
      return p.map(part => {
        if (part.t === MathPartTypes.tag && part.v === "br") {
          return { t: MathPartTypes.text, v: "\n" };
        }
        return part;
      });
    }
  }

  private concatTexts(parts: MathPart[]): MathPart[] {
    const result: MathPart[] = [];

    parts.reduce((previousValue: MathPart | null, currentValue) => {
      if (
        previousValue &&
        previousValue.t === MathPartTypes.text &&
        currentValue.t === MathPartTypes.text
      ) {
        previousValue.v += currentValue.v;
        return previousValue;
      }
      result.push(currentValue);
      return currentValue;
    }, null);

    return result;
  }

  dom2MathParts(useSelection: boolean): MathPart[] {
    const self = this;

    let selection: Selection | null;

    if (useSelection) {
      selection = document.getSelection();
      if (!selection) {
        return [];
      }
    } else {
      selection = null;
    }

    let firstBoundaryFound = false;

    const ret: MathPart[] = [];

    this.nodeManager.forEachChild(
      this.element,
      {
        visitLineBreak() {
          ret.push({ t: MathPartTypes.tag, v: "br" });
          firstBoundaryFound = true;
        },
        visitServiceBreak() {},
        visitCell(id: string, editor: Editor) {
          const e = { t: MathPartTypes.math, v: editor.getInternal() };
          ret.push(e);
          firstBoundaryFound = true;
        },
        visitText(child) {
          if (child.textContent) {
            if (selection) {
              if (
                child == selection.anchorNode ||
                child == selection.focusNode
              ) {
                let shift =
                  child == selection.anchorNode
                    ? selection.anchorOffset
                    : selection.focusOffset;
                if (!firstBoundaryFound) {
                  if (selection.anchorNode === selection.focusNode) {
                    //selection inside one node
                    const start = Math.min(
                      selection.anchorOffset,
                      selection.focusOffset
                    );
                    const end = Math.max(
                      selection.anchorOffset,
                      selection.focusOffset
                    );
                    ret.push({
                      t: MathPartTypes.text,
                      v: child.textContent
                        .substr(start, end - start)
                        .replace("\u00A0", " ")
                    });
                  } else {
                    ret.push({
                      t: MathPartTypes.text,
                      v: child.textContent.substr(shift).replace("\u00A0", " ")
                    });
                  }
                  firstBoundaryFound = true;
                } else {
                  ret.push({
                    t: MathPartTypes.text,
                    v: child.textContent.substr(0, shift).replace("\u00A0", " ")
                  });
                }
              } else {
                ret.push({
                  t: MathPartTypes.text,
                  v: child.textContent.replace("\u00A0", " ")
                });
              }
            } else {
              ret.push({
                t: MathPartTypes.text,
                v: child.textContent.replace("\u00A0", " ")
              });
            }
          }
        },
        visitDelimiter() {}
      },
      node => {
        return !selection || selection.getRangeAt(0).intersectsNode(node);
      }
    );

    return this.concatTexts(ret);

 
  }

  onCursorPlace(editor: Editor): void {
    this.editorManager.forEach(ed => {
      if (ed !== editor) {
        ed.deselect();
      }
    });
    const selection = document.getSelection();
    if (selection) {
      selection.empty();
    }
  }

  onInput() {
    const self = this;

    let element = this.$refs.editor as Element;

    this.cleaner.clean(element);

    this.forEach(
      editor => {
        const el = editor.element;
        tryCreateDelimiterAfter(el);
        tryCreateDelimiterBefore(el);
      },
      text => {},
      delim => {},
      br => {
        tryCreateDelimiterBefore(br);
      }
    );

    let empty = true;

    this.nodeManager.forEachChild(this.element, {
      visitCell() {
        empty = false;
        return true;
      },
      visitText() {
        empty = false;
        return true;
      },
      visitLineBreak() {
        empty = false;
        return true;
      },
      visitDelimiter(delim) {
        //If the text ends with br, the latest delimiter must have display block, otherwise it is not possible
        //to place the cursor at the next line
        if (
          !delim.nextSibling &&
          delim.previousSibling &&
          self.nodeManager.isBreak(delim.previousSibling)
        ) {
          delim.style.display = "block";
        } else {
          delim.style.display = "inline";
        }
        //continue
        return true;
      },
      visitServiceBreak() {
        //continue
        return true;
      }
    });

    //workaround for firefox bug on empty contenteditable
    //https://www.bountysource.com/issues/2378086-cursor-position-bug-in-firefox
    //Does not affect Chrome
    if (empty) {
      for (let i = element.childNodes.length - 1; i >= 0; i--) {
        element.childNodes.item(i).remove();
      }
      const br = this.nodeManager.createServiceLineBreak();
      element.appendChild(br);
    }

    function tryCreateDelimiterAfter(el: Node) {
      if (needDelimiter(el.previousSibling, true)) {
        if (el.parentElement) {
          let n = self.nodeManager.createDelimiter();
          el.parentElement.insertBefore(n, el);
          self.cursorSet.setCursor(n, false);
        }
      }
    }

    function tryCreateDelimiterBefore(el: Node) {
      if (needDelimiter(el.nextSibling, false)) {
        if (el.parentElement) {
          let n = self.nodeManager.createDelimiter();
          el.parentElement.insertBefore(n, el.nextSibling);
          self.cursorSet.setCursor(n, false);
        }
      }
    }

    function needDelimiter(node: Node | null, after: boolean): boolean {
      if (!node) {
        return true;
      } else {
        return self.nodeManager.determineType<boolean>(node, {
          visitCell() {
            return true;
          },
          visitText() {
            return false;
          },
          visitDelimiter() {
            return false;
          },
          visitServiceBreak() {
            //returns true if we are AFTER the br and flase if we are before
            return after;
          },
          visitLineBreak() {
            return false;
          }
        });
      }
    }
  }

  private getLatestCellContent():
    | { content: string; isGrl: boolean }
    | undefined {
    let lstEditor: Editor | undefined;
    let stop = false;

    const ap = this.getAnchorPosition();

    this.forEach(
      ed => {
        if (!stop) {
          lstEditor = ed;
        }
        if (ap === ed) {
          stop = true;
        }
      },
      t => {
        if (ap === t) {
          stop = true;
        }
      },
      d => {
        if (ap === d) {
          stop = true;
        }
      },
      b => {
        if (ap === b) {
          stop = true;
        }
      }
    );

    if (lstEditor) {
      return { content: lstEditor.getInternal(), isGrl: false };
    } else {
      if (this.initialInsert) {
        for (let i = this.initialInsert.length - 1; i >= 0; i--) {
          const p = this.initialInsert[i];
          if (p.t === MathPartTypes.math) {
            return { content: p.v, isGrl: false };
          }
          if (p.t === MathPartTypes.grl) {
            return { content: p.v, isGrl: true };
          }
        }
      }
    }
  }

  onKeyDown(e: KeyboardEvent): boolean {
    const self = this;
    if (this.eventHandled) {
      this.eventHandled = false;
      return true;
    }

    if (
      (e.key === "ArrowUp" || e.key === "ArrowDown") &&
      this.getAnchorPlace() === Place.text
    ) {
      return handleUpDown();
    }

    if (
      (e.keyCode == KEY_CODES.RIGHT_ARROW ||
        e.keyCode == KEY_CODES.LEFT_ARROW) &&
      this.getAnchorPlace() == Place.text
    ) {
      return handleLeftRight();
    }

    if (e.key === "Home" || e.key === "End") {
      return true;
    }

    if (
      (e.keyCode === KEY_CODES.LETTER_C ||
        e.keyCode === KEY_CODES.LETTER_X ||
        e.keyCode === KEY_CODES.LETTER_A) &&
      e.ctrlKey
    ) {
      return true;
    }

    if (this.readonly) {
      e.preventDefault();
      return false;
    }

    if (e.keyCode == KEY_CODES.WHITESPACE) {
      return handleSpace();
    }

    if (e.key === "Insert") {
      return handleInsert();
    }

    if (e.key === "Enter") {
      return handleEnter();
    }

    if (e.key == "Delete" || e.key == "Backspace") {
      return handleDeleteBackspace();
    }

    return true;

    function handleSpace() {
      //Workaround for firefox bug with trailing spaces.
      if (self.getAnchorPlace() == Place.text) {
        const ws = document.createTextNode("\u00A0");

        const sel = document.getSelection();
        if (sel) {
          sel.getRangeAt(0).insertNode(ws);
          sel.removeAllRanges();

          const range = document.createRange();
          range.setStart(ws, 1);
          range.collapse(true);
          sel.addRange(range);
        }
        //looks like preventDefault prevents onInput call as well
        self.onInput();
        e.preventDefault();
        return false;
      }

      return true;
    }

    function handleInsert() {
      if (self.getAnchorPlace() == Place.text) {
        const id = newGuid();
        const editor = self.editorManager.createNewEditor(id,!!self.readonly,!!self.wrap);

        if (e.ctrlKey || e.shiftKey) {
          const content = self.getLatestCellContent();
          if (content) {
            if (content.isGrl) {
              editor.setGRL(content.content);
            } else {
              editor.setInternal(content.content);
            }
          }
        }

        self.deleteAllSelected();
        self.insertNode(editor.element);
        self.onInput();

        //workaround
        editor.deactivate();

        self.cursorSet.setCursor(editor.element, e.ctrlKey);

        e.preventDefault();

        return false;
      }

      return true;
    }

    function handleEnter() {
      if (self.getAnchorPlace() == Place.text) {
        const br = self.nodeManager.createLineBreak();
        self.deleteAllSelected();
        self.insertNode(br);
        self.onInput();

        self.cursorSet.setCursor(br, true);

        e.preventDefault();
        return false;
      }

      return true;
    }

    function handleDeleteBackspace() {
      const selection = document.getSelection();
      if (
        selection &&
        selection.type == "Range" &&
        self.getAnchorPlace() == Place.text
      ) {
        self.deleteAllSelected();
        self.onInput();
        e.preventDefault();
        return false;
      } else {
        if (self.getAnchorPlace() == Place.text) {
          const right = e.keyCode == KEY_CODES.DEL;

          const eventHandled = self.cursorMoveHor.proceedDeletion(right);

          if (eventHandled) {
            self.onInput();
            e.preventDefault();
          }
        }
      }

      return true;
    }

    function handleUpDown() {
      const up = e.key === "ArrowUp";
      const eventHandled = self.cursorMoveVert.moveCurrentTextCursorVertically(
        up
      );
      if (eventHandled) {
        e.preventDefault();
        return false;
      }

      return true;
    }

    function handleLeftRight() {
      const right = e.keyCode == KEY_CODES.RIGHT_ARROW;
      if (!e.shiftKey) {
        const eventHandled = self.cursorMoveHor.moveCursor(right);
        if (eventHandled) {
          e.preventDefault();
        }
      } else {
        //selection with the keyboard
        selectWithKeyboard(right);
        e.preventDefault();
        return false;
      }

      return true;
    }

    function selectWithKeyboard(right: boolean) {
      const selection = document.getSelection();
      if (selection) {
        let focusNode = selection.focusNode;
        if (focusNode && selection.anchorNode) {
          // Как меняем
          let target: Node | null = null;
          if (focusNode === self.element) {
            console.log(
              "focus is texteditor " +
                selection.focusOffset +
                "/" +
                selection.anchorOffset
            );

            // если focus==element , то это не текст.
            //Поэтому берем брата от element.chilNodes.item(focusOffset + или - один) Это смотря куда движется курсор (вправо 0, влево -1)
            //Важно - offset - это не индекс в массиве детей. Это место между ними. От 0 (перед первым) до length (после последнего)
            const siblingIndex = selection.focusOffset + (right ? 0 : -1);
            if (
              siblingIndex >= 0 &&
              siblingIndex < self.element.childNodes.length
            ) {
              target = self.element.childNodes.item(siblingIndex);
              console.log(target);
            }
            // Направление самого selection мне тут вообще не важно.
          } else {
            // если focus!=element то это текст.
            const text = focusNode as Text;
            const textContent = text.textContent ? text.textContent : "";
            console.log("focus is not TextEditor");
            if (text) {
              console.log("focus is text");
              if (selection.focusOffset === 0 && !right) {
                //левый край, выходим наружу
                if (focusNode.previousSibling) {
                  //если на краю, то взять брата этого текста также, как описано выше. И далее сделать все точно также.
                  target = focusNode.previousSibling;
                  console.log("left corner target: " + target);
                }
              } else if (
                selection.focusOffset === textContent.length &&
                right
              ) {
                //правый край, выходим наружу
                if (focusNode.nextSibling) {
                  //если на краю, то взять брата этого текста также, как описано выше. И далее сделать все точно также.
                  target = focusNode.nextSibling;
                  console.log("right corner target: " + target);
                }
              } else {
                //остаемся внутри этого нода
                // увеличить или уменьшить focusOffset, а focus оставить
                selection.extend(focusNode,selection.focusOffset + (right ? 1 : -1));
                console.log("inside text");
              }
            }
          }
          if (target) {
            while (hasZeroWidth(target)) {
              if (right) {
                target = target.nextSibling;
              } else {
                target = target.previousSibling;
              }
              if (target === null) {
                break;
              }
            }
            if (target) {
              // Далее надо смотреть что это за брат.
              // Если не текст то
              if (target.nodeType !== Node.TEXT_NODE) {
                //focus = element,
                console.log("target is not text");
                let offset: number = -1;
                for (
                  offset = 0;
                  offset < self.element.childNodes.length;
                  offset++
                ) {
                  if (self.element.childNodes.item(offset) === target) {
                    break;
                  }
                }
                console.log("target's index " + offset);
                if (offset >= 0) {
                 
                  selection.extend(self.element,offset + (right ? 1 : 0));
                }
              } else {
                console.log("target is text");
                // Если текст, focus = text, focusOffset = 1 или длина минус один (1 если движемся вправо).
                const text = target as Text;
                const textContent = text.textContent ? text.textContent : "";
                const index = right ? 1 : textContent.length - 1;
                console.log("setting focus index to " + index);
               
                selection.extend(target,index);
              }
            }
          }
        }
      }
    }
    function hasZeroWidth(node: Node): boolean {
      return self.nodeManager.determineType(node, {
        visitServiceBreak() {
          return true;
        },
        visitCell() {
          return false;
        },
        visitText() {
          return false;
        },
        visitDelimiter() {
          return true;
        },
        visitLineBreak() {
          return true;
        }
      });
    }
  }

  private deleteAllSelected(): boolean {
    const selection = document.getSelection();
    if (selection) {
      const range = selection.getRangeAt(0);
      if (selection.type === "Range") {
        this.editorManager.forEach(editor => {
          if (selection.containsNode(editor.element, true)) {
            this.editorManager.delete(editor);
          }
        });
        range.deleteContents();
        range.collapse(true);
        return true;
      }
    }

    return false;
  }

  private insertNode(node: Node) {
    const selection = document.getSelection();
    if (selection) {
      if (selection.type === "Caret") {
        const range = selection.getRangeAt(0);
        range.insertNode(node);
      }
    }
  }

  getMathText(): MathPart[] {
    return this.dom2MathParts(false);
  }

  getEditor(id: string): Editor | undefined {
    return this.editorManager.getEditor(id);
  }
}
